import { createRootRoute, createRoute, lazyRouteComponent } from '@tanstack/react-router';

const NotFoundPage = lazyRouteComponent(() => import('../pages/not-found'));
const LoginPage = lazyRouteComponent(() => import('../pages/login'));
const HomePage = lazyRouteComponent(() => import('../pages/home'));
const SubscriptionsPage = lazyRouteComponent(() => import('../pages/subscriptions'));
const TrackedSubjectEventsRedirectPage = lazyRouteComponent(() => import('../pages/tracked-subject-events'));
const ProfileDetailPage = lazyRouteComponent(() => import('../pages/profile-detail'));
const OrganizationDetailPage = lazyRouteComponent(() => import('../pages/organization-detail'));
const ProfileRequestsPage = lazyRouteComponent(() => import('../pages/profile-requests'));
const RegistrationPage = lazyRouteComponent(() => import('../pages/registration'));
const ResetPasswordPage = lazyRouteComponent(() => import('../pages/reset-password'));
const ConfirmResetPasswordPage = lazyRouteComponent(() => import('../pages/confirm-reset-password'));
const AdminPage = lazyRouteComponent(() => import('../pages/admin'));
const AdminAccountsPage = lazyRouteComponent(() => import('../pages/admin-accounts'));
const AdminAccountDetailPage = lazyRouteComponent(() => import('../pages/admin-account-detail'));
const AdminUsersPage = lazyRouteComponent(() => import('../pages/admin-users'));
const AdminUserDetailPage = lazyRouteComponent(() => import('../pages/admin-user-detail'));
const AdminProfilesPage = lazyRouteComponent(() => import('../pages/admin-profiles'));
const AdminProfileDetailPage = lazyRouteComponent(() => import('../pages/admin-profile-detail'));
const AdminOrganizationsPage = lazyRouteComponent(() => import('../pages/admin-organizations'));
const AdminOrganizationDetailPage = lazyRouteComponent(() => import('../pages/admin-organization-detail'));
const AdminInterestsPage = lazyRouteComponent(() => import('../pages/admin-interests'));
const AdminIndustriesPage = lazyRouteComponent(() => import('../pages/admin-industries'));
const AdminBusinessFunctionsPage = lazyRouteComponent(() => import('../pages/admin-business-functions'));
const AdminQuartrEventTranscriptsPage = lazyRouteComponent(() => import('../pages/admin-quartr-event-transcripts'));
const AdminQuartrEventTranscriptDetailPage = lazyRouteComponent(
  () => import('../pages/admin-quartr-event-transcript-detail'),
);
const AdminRequestsPage = lazyRouteComponent(() => import('../pages/admin-requests'));
const AdminRequestsKanbanPage = lazyRouteComponent(() => import('../pages/admin-requests-kanban'));
const AdminRequestDetailPage = lazyRouteComponent(() => import('../pages/admin-request-detail'));
const AdminDashboardsPage = lazyRouteComponent(() => import('../pages/admin-dashboards'));
const ValueDashboardPage = lazyRouteComponent(() => import('../pages/value-dashboard'));
const AdminEvaluationsPage = lazyRouteComponent(() => import('../pages/admin-evaluations'));
const AdminEvaluationDetailPage = lazyRouteComponent(() => import('../pages/admin-evaluation-detail'));
const AdminEvaluationMetricDetailPage = lazyRouteComponent(() => import('../pages/admin-evaluation-metric-detail'));
const RelationshipMappingPage = lazyRouteComponent(() => import('../pages/relationship-mapping'));
const ProfilePage = lazyRouteComponent(() => import('../pages/profile'));

import { Routes } from 'shared/routes';

import { AdminEvaluationsAccessLayout, AdminLayout, ClientLayout, RootLayout } from './layouts';

// Define the root route
export const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
});

// Login route
export const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Routes.frontend.loginPath,
  component: LoginPage,
});

export const registrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Routes.frontend.registrationPath,
  component: RegistrationPage,
});

export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Routes.frontend.resetPasswordPath,
  component: ResetPasswordPage,
});

export const confirmResetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: Routes.frontend.confirmResetPasswordPath,
  component: ConfirmResetPasswordPage,
});

export const adminLayoutRoute = createRoute({
  id: 'adminLayout',
  getParentRoute: () => rootRoute,
  component: AdminLayout,
});

export const clientLayoutRoute = createRoute({
  id: 'clientLayout',
  getParentRoute: () => rootRoute,
  component: ClientLayout,
});

export const clientSearchRoute = createRoute({
  getParentRoute: () => clientLayoutRoute,
  path: Routes.frontend.rootPath,
  component: HomePage,
});

export const clientSubscriptionsRoute = createRoute({
  getParentRoute: () => clientLayoutRoute,
  path: Routes.frontend.subscriptionsPath,
  component: SubscriptionsPage,
});

export const trackedSubjectEventsRoute = createRoute({
  getParentRoute: () => clientLayoutRoute,
  path: '/subscriptions/tracked-subjects/$trackedSubjectId/events',
  component: TrackedSubjectEventsRedirectPage,
});

// Profile detail route
export const profileDetailRoute = createRoute({
  getParentRoute: () => clientLayoutRoute,
  path: '/profiles/$profileId',
  component: ProfileDetailPage,
});

// Organization detail route
export const organizationDetailRoute = createRoute({
  getParentRoute: () => clientLayoutRoute,
  path: '/organizations/$organizationId',
  component: OrganizationDetailPage,
});

// Profile requests route
export const profileRequestsRoute = createRoute({
  getParentRoute: () => clientLayoutRoute,
  path: Routes.frontend.profileRequestsPath,
  component: ProfileRequestsPage,
});

export const relationshipsRoute = createRoute({
  getParentRoute: () => clientLayoutRoute,
  path: Routes.frontend.relationshipsPath,
  component: RelationshipMappingPage,
});

export const profileRoute = createRoute({
  getParentRoute: () => clientLayoutRoute,
  path: Routes.frontend.userProfilePath,
  component: ProfilePage,
});

// Admin routes
export const adminRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminPath,
  component: AdminPage,
});

export const adminAccountsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminAccountsPath,
  component: AdminAccountsPage,
});

export const adminAccountDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/admin/accounts/$accountId',
  component: AdminAccountDetailPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminUsersPath,
  component: AdminUsersPage,
});

export const adminUserDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/admin/users/$userId',
  component: AdminUserDetailPage,
});

const adminProfilesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminProfilesPath,
  component: AdminProfilesPage,
});

export const adminProfileDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/admin/profiles/$profileId',
  component: AdminProfileDetailPage,
});

const adminOrganizationsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminOrganizationsPath,
  component: AdminOrganizationsPage,
});

export const adminOrganizationDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/admin/organizations/$organizationId',
  component: AdminOrganizationDetailPage,
});

const adminInterestsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminInterestsPath,
  component: AdminInterestsPage,
});

const adminIndustriesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminIndustriesPath,
  component: AdminIndustriesPage,
});

const adminBusinessFunctionsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminBusinessFunctionsPath,
  component: AdminBusinessFunctionsPage,
});

const adminQuartrEventTranscriptsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminQuartrEventTranscriptsPath,
  component: AdminQuartrEventTranscriptsPage,
});

const adminQuartrEventTranscriptDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/admin/quartr-event-transcripts/$transcriptId',
  component: AdminQuartrEventTranscriptDetailPage,
});

const adminRequestsBoardRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminRequestsBoardPath,
  component: AdminRequestsKanbanPage,
});

const adminRequestsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminRequestsPath,
  component: AdminRequestsPage,
});

const adminRequestDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/admin/requests/$id',
  component: AdminRequestDetailPage,
});

const adminDashboardsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminDashboardsPath,
  component: AdminDashboardsPage,
});

const adminValueDashboardRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: Routes.frontend.adminValueDashboardPath,
  component: ValueDashboardPage,
});

const adminEvaluationsLayoutRoute = createRoute({
  id: 'adminEvaluationsLayout',
  getParentRoute: () => adminLayoutRoute,
  component: AdminEvaluationsAccessLayout,
});

const adminEvaluationsRoute = createRoute({
  getParentRoute: () => adminEvaluationsLayoutRoute,
  path: Routes.frontend.adminEvaluations,
  component: AdminEvaluationsPage,
});

const adminEvaluationDetailRoute = createRoute({
  getParentRoute: () => adminEvaluationsLayoutRoute,
  path: '/admin/evaluations/$evaluationId',
  component: AdminEvaluationDetailPage,
});

const adminEvaluationMetricDetailRoute = createRoute({
  getParentRoute: () => adminEvaluationsLayoutRoute,
  path: '/admin/evaluation-metrics/$metricId',
  component: AdminEvaluationMetricDetailPage,
});

// Create the route tree
export const routeTree = rootRoute.addChildren([
  signInRoute,
  registrationRoute,
  clientLayoutRoute.addChildren([
    clientSearchRoute,
    clientSubscriptionsRoute,
    trackedSubjectEventsRoute,
    profileDetailRoute,
    organizationDetailRoute,
    profileRequestsRoute,
    relationshipsRoute,
    profileRoute,
  ]),
  resetPasswordRoute,
  confirmResetPasswordRoute,
  adminLayoutRoute.addChildren([
    adminRoute,
    adminAccountsRoute,
    adminAccountDetailRoute,
    adminUsersRoute,
    adminUserDetailRoute,
    adminProfilesRoute,
    adminProfileDetailRoute,
    adminOrganizationsRoute,
    adminOrganizationDetailRoute,
    adminInterestsRoute,
    adminIndustriesRoute,
    adminBusinessFunctionsRoute,
    adminQuartrEventTranscriptsRoute,
    adminQuartrEventTranscriptDetailRoute,
    adminRequestsBoardRoute,
    adminRequestsRoute,
    adminRequestDetailRoute,
    adminDashboardsRoute,
    adminValueDashboardRoute,
    adminEvaluationsLayoutRoute.addChildren([
      adminEvaluationsRoute,
      adminEvaluationDetailRoute,
      adminEvaluationMetricDetailRoute,
    ]),
  ]),
]);
